import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Sound = {
  context: AudioContext;
  master: GainNode;
  oscillators: OscillatorNode[];
  voices: GainNode[];
  startedAt: number;
  closeTimer: ReturnType<typeof setTimeout> | null;
};

const notes = [174.61, 220, 261.63, 329.63];

function browserSupportsAudio() {
  return typeof window !== "undefined" && typeof window.AudioContext === "function";
}

export function CelestialAudio({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const sound = useRef<Sound | null>(null);
  const generation = useRef(0);
  const supported = browserSupportsAudio();

  const stop = useCallback((fade = true) => {
    generation.current += 1;
    const current = sound.current;
    sound.current = null;
    setPlaying(false);
    if (!current) return;
    if (current.closeTimer) clearTimeout(current.closeTimer);
    const { context, master, oscillators, voices } = current;
    try {
      const now = context.currentTime;
      const currentVolume = 0.018 * Math.min(1, Math.max(0, (now - current.startedAt) / 1.2));
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(currentVolume, now);
      master.gain.linearRampToValueAtTime(0, now + (fade ? 0.35 : 0));
      oscillators.forEach((oscillator) => oscillator.stop(now + (fade ? 0.4 : 0)));
    } catch {
      oscillators.forEach((oscillator) => {
        try { oscillator.stop(); } catch { /* Already stopped. */ }
      });
    }
    current.closeTimer = setTimeout(() => {
      oscillators.forEach((oscillator) => oscillator.disconnect());
      voices.forEach((voice) => voice.disconnect());
      master.disconnect();
      void context.close().catch(() => {});
    }, fade ? 450 : 0);
  }, []);

  const start = useCallback(async () => {
    if (!enabled || !supported || sound.current) return;
    const AudioContextConstructor = window.AudioContext;
    if (!AudioContextConstructor) return;
    const request = ++generation.current;
    let allocatedContext: AudioContext | null = null;
    let allocatedMaster: GainNode | null = null;
    const allocatedOscillators: OscillatorNode[] = [];
    const allocatedVoices: GainNode[] = [];
    let current: Sound | null = null;
    try {
      const context = new AudioContextConstructor();
      allocatedContext = context;
      const master = context.createGain();
      allocatedMaster = master;
      master.gain.setValueAtTime(0, context.currentTime);
      master.gain.linearRampToValueAtTime(0.018, context.currentTime + 1.2);
      master.connect(context.destination);
      notes.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        allocatedOscillators.push(oscillator);
        const voice = context.createGain();
        allocatedVoices.push(voice);
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;
        voice.gain.value = index === 0 ? 0.46 : 0.24;
        oscillator.connect(voice);
        voice.connect(master);
        oscillator.start();
      });
      current = {
        context,
        master,
        oscillators: allocatedOscillators,
        voices: allocatedVoices,
        startedAt: context.currentTime,
        closeTimer: null,
      };
      sound.current = current;
      setPlaying(true);
      await context.resume();
      if (generation.current !== request || sound.current !== current || !enabled) {
        if (current.closeTimer) clearTimeout(current.closeTimer);
        if (sound.current === current) sound.current = null;
        allocatedOscillators.forEach((oscillator) => {
          try { oscillator.stop(); } catch { /* Already stopped. */ }
          oscillator.disconnect();
        });
        allocatedVoices.forEach((voice) => voice.disconnect());
        master.disconnect();
        if (context.state !== "closed") await context.close().catch(() => {});
        return;
      }
    } catch {
      const stale = generation.current !== request;
      if (current?.closeTimer) clearTimeout(current.closeTimer);
      if (current && sound.current === current) sound.current = null;
      allocatedOscillators.forEach((oscillator) => {
        try { oscillator.stop(); } catch { /* Already stopped. */ }
        oscillator.disconnect();
      });
      allocatedVoices.forEach((voice) => voice.disconnect());
      allocatedMaster?.disconnect();
      if (allocatedContext?.state !== "closed") await allocatedContext?.close().catch(() => {});
      if (stale) return;
      setFailed(true);
      setPlaying(false);
    }
  }, [enabled, stop, supported]);

  useEffect(() => {
    if (!enabled) stop();
  }, [enabled, stop]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") stop();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stop(false);
    };
  }, [stop]);

  if (!enabled) return null;

  const unavailable = !supported || failed;
  return (
    <div className="celestial-audio-control">
      <button
        type="button"
        className="celestial-audio-control__button"
        aria-label={t(playing ? "holy.audioStop" : "holy.audioStart")}
        aria-pressed={playing}
        disabled={unavailable}
        onClick={() => playing ? stop() : void start()}
      >
        {t(playing ? "holy.audioStop" : "holy.audioStart")}
      </button>
      {unavailable ? <span className="celestial-audio-control__message" role="status">{t("holy.audioUnavailable")}</span> : null}
    </div>
  );
}
